import { AlertSeverity, Analysis, AnalysisSummary, AnalysisType, Job, SnapshotType } from '@analyzer/client';
import { WebRequest } from '@analyzer/core/lib/collector/collectors/web.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useService } from '../providers/ServiceProvider';

interface CreateArguments {
  projectId: string;
  snapshotId: string;
  snapshotType: SnapshotType;
  context: number;
  severityThreshold: AlertSeverity;
  announce: boolean;
}

export function useAnalysisCreateMutation() {
  const client = useService();
  const queryClient = useQueryClient();
  return useMutation<Job, Error, CreateArguments>({
    mutationFn: ({ projectId, snapshotId, snapshotType, context, severityThreshold, announce }) =>
      client.analysisCreate(projectId, snapshotId, snapshotType, { context, severityThreshold, announce }),
    onSuccess: () => queryClient.invalidateQueries([`analysis-list`])
  });
}

type QueryParams = { limit?: number; offset?: number };

export function useAnalysisListQuery(
  type: AnalysisType,
  projectId: string,
  resourceId: string,
  queryParams: QueryParams = {}
) {
  const client = useService();
  return useQuery<AnalysisSummary[], Error>({
    queryKey: [`${type}-analysis-list`, projectId, resourceId, queryParams],
    queryFn: () => client.analysisList(type, projectId, resourceId, queryParams),
    keepPreviousData: true
  });
}

export function useAnalysisDestroyMutation(analysisType: AnalysisType) {
  const client = useService();
  const queryClient = useQueryClient();

  return useMutation<boolean, Error, { projectId: string; analysisId: string }>({
    mutationFn: ({ projectId, analysisId }) => client.analysisDestroy(projectId, analysisId, analysisType),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [`${analysisType}-analysis-list`] })
  });
}

export interface AnalysisDebug {
  snapshots: number[];
  seen: WebRequest[];
  fresh: WebRequest[];
  matches: {
    r0: WebRequest;
    r1: WebRequest;
  }[];
  additional: WebRequest[];
  missing: WebRequest[];
}

export function useAnalysisDebugQuery(projectId: string, analysisId: string, analysisType: AnalysisType) {
  const client = useService();
  return useQuery<AnalysisDebug, Error>({
    queryKey: [`analysis-debug-read`, projectId, analysisId],
    queryFn: async () => {
      const response = await client.fetch(
        `/api/projects/${projectId}/analyses/${analysisType}/${analysisId}/debug`
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch alerts: ${response.statusText}`);
      }
      return response.json();
    }
  });
}

export function useAnalysisReadQuery(projectId: string, analysisId: string, analysisType: AnalysisType) {
  const client = useService();
  return useQuery<Analysis, Error>({
    queryKey: [`analysis-read`, projectId, analysisId, analysisType],
    queryFn: () => client.analysisRead(projectId, analysisId, analysisType)
  });
}
